/**
 * @file CompanyRateFieldsSection.tsx
 * @brief Per-product custom-pricing inputs for the company dialogs.
 *
 * @details
 * Renders one compact card per product (Interpretation, Realtime, Meeting
 * Intel, Personal Notes), each with a currency header row (USD/EUR/GBP) and a
 * labelled row per sub-field (per-minute, min charge, per speaker). Driven by
 * `COMPANY_RATE_PRODUCTS` so Create and Edit stay identical. Blank = inherit
 * the platform rate. Each product card is collapsible (26-07-2026).
 *
 * The parent owns the react-hook-form instance; we only need its `register`
 * for the major-unit fields (`custom_*_major`).
 *
 * @author DES
 * @date 06-07-2026
 */

import { BARE_FIELDS, FieldStyleContext } from "@/components/ui/fieldStyle";
import { useState } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";
import { ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { cn } from "@/shared/theme/cn";
import { COMPANY_RATE_PRODUCTS, CURRENCY_CODES, type MajorFieldName } from "../companyRateFields";
import type { SystemSettingsResponse } from "@/features/billing/schemas";

interface CompanyRateFieldsSectionProps {
  /**
   * react-hook-form `register`, narrowed to the major-unit override fields.
   * Both dialogs' `form.register` satisfy this (their FormValues carry a
   * `custom_${string}_major` index signature).
   */
  register: (name: MajorFieldName) => UseFormRegisterReturn;
  /**
   * Platform pricing from the Billing & Pricing page (`system_settings`).
   * Shown as each input's placeholder so admins see the general rate a blank
   * field inherits.
   */
  platformSettings: SystemSettingsResponse | undefined;
  /**
   * When true, skip the intro blurb — parent Section already explains PAYG
   * overrides (Edit company modal segregation).
   */
  embedded?: boolean;
}

/** Minor-unit setting value → major-unit display string (e.g. "20" → "0.20"). */
function settingMajor(minor: string | undefined): string {
  if (minor == null || minor === "") return "";
  const n = Number(minor);
  if (!Number.isFinite(n)) return "";
  return (n / 100).toFixed(2);
}

export function CompanyRateFieldsSection({
  register,
  platformSettings,
  embedded = false,
}: CompanyRateFieldsSectionProps) {
  const settings = platformSettings?.settings ?? {};
  // Start collapsed so Create/Edit company dialogs stay short (26-07-2026).
  const [openByProduct, setOpenByProduct] = useState<Record<string, boolean>>({});

  const toggle = (product: string) => {
    setOpenByProduct((prev) => ({
      ...prev,
      [product]: !prev[product],
    }));
  };

  return (
    // The app's bare field, as every form field (DES 01-10-2026: "fields
    // style match karoo"). No label in the context, so each input keeps its
    // placeholder: the platform default it falls back to.
    <FieldStyleContext.Provider value={{ bare: BARE_FIELDS }}>
      <div className="space-y-1.5">
        {embedded ? null : (
          <p className="text-xs text-muted-foreground">
            Faded numbers are the platform default from the Billing page. Leave a field blank to
            keep that default; type a value to override it for this company. Expand a product to
            edit rates.
          </p>
        )}

        {/* Two products side by side (DES 01-10-2026: "two in one row"), as
            two independent columns: opening one moves only what is below it
            in its own column ("jo open ho, sirf wohi move hoo"). Left takes
            the 1st and 3rd product, right the 2nd and 4th. */}
        <div className="grid items-start gap-x-8 gap-y-1.5 md:grid-cols-2">
          {[0, 1].map((col) => (
            <div key={col} className="space-y-1.5">
              {COMPANY_RATE_PRODUCTS.filter((_, i) => i % 2 === col).map((p) => {
                const open = !!openByProduct[p.product];
                const panelId = `company-rate-${p.product.replace(/\s+/g, "-").toLowerCase()}`;

                return (
                  // Tree style, like the grouped lists (DES 01-10-2026: "is section
                  // bhi tree style main karo"): a group row with the chevron on the
                  // left and a light fill, no card; open, its rates hang off a rail
                  // under the chevron.
                  <div key={p.product}>
                    <button
                      type="button"
                      onClick={() => toggle(p.product)}
                      aria-expanded={open}
                      aria-controls={panelId}
                      className="flex w-full items-center gap-3 rounded-lg bg-muted/45 px-3 py-2.5 text-left transition-colors hover:bg-muted/60"
                    >
                      <ChevronRight
                        className={cn(
                          "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                          open && "rotate-90",
                        )}
                      />
                      <span className="text-sm font-semibold">{p.product}</span>
                    </button>

                    {open ? (
                      <div
                        id={panelId}
                        className="ml-[1.25rem] space-y-2 border-l border-app-text/20 pb-3 pl-5 pt-2"
                      >
                        <div className="grid grid-cols-[5.5rem_repeat(3,minmax(0,1fr))] items-center gap-x-2 gap-y-1.5">
                          <span />
                          {CURRENCY_CODES.map((c) => (
                            <span
                              key={`h-${c}`}
                              className="text-[11px] font-medium text-muted-foreground"
                            >
                              {c.toUpperCase()}
                            </span>
                          ))}

                          {p.fields.map((f) => (
                            <FieldRow key={f.label} label={f.label}>
                              {CURRENCY_CODES.map((c) => {
                                const fallback = settingMajor(settings[f.settingKey(c)]?.value);
                                return (
                                  <div
                                    key={f.majorKey(c)}
                                    className={
                                      BARE_FIELDS
                                        ? "border-b border-app-text/10 pb-2 pt-1"
                                        : undefined
                                    }
                                  >
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min={0}
                                      placeholder={fallback || "—"}
                                      {...register(f.majorKey(c) as MajorFieldName)}
                                    />
                                  </div>
                                );
                              })}
                            </FieldRow>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </FieldStyleContext.Provider>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </>
  );
}
